import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dailf4bp14a1h3mr7670';

export default function SubscribeSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功，热点周报会发送到你的邮箱。');
      return;
    }
    payload.delete('website_alt');

    const email = String(payload.get('email') || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('请填写有效的邮箱地址。');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setMessage('订阅成功，热点周报会发送到你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，请稍后重试。');
    }
  };

  return (
    <section id="subscribe" className="bg-secondary-900 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="font-label text-xs tracking-[0.25em] text-primary-300">NEWSLETTER</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              订阅热点周报，不掉队
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-secondary-200">
              每周一封，汇总本周最值得补课的话题与要点。不发广告，随时可退订。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-secondary-100">
              <li className="flex items-center gap-2.5">
                <i className="ri-checkbox-circle-line text-primary-300" aria-hidden="true"></i>
                本周热度榜与话题脉络
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-checkbox-circle-line text-primary-300" aria-hidden="true"></i>
                编辑精选的深度观察
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-checkbox-circle-line text-primary-300" aria-hidden="true"></i>
                新话题上线的第一时间提醒
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              id="weekly-digest-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-secondary-700 bg-secondary-950/40 p-5 md:p-7"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label htmlFor="subscribe-email" className="font-label text-xs text-secondary-200">
                    邮箱地址
                  </label>
                  <input
                    id="subscribe-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="mt-2 w-full rounded-md border border-secondary-700 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                  />
                </div>
                <div className="sm:w-40">
                  <label htmlFor="subscribe-interest" className="font-label text-xs text-secondary-200">
                    关注方向
                  </label>
                  <select
                    id="subscribe-interest"
                    name="interest"
                    defaultValue="全部方向"
                    className="mt-2 w-full rounded-md border border-secondary-700 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                  >
                    <option>全部方向</option>
                    <option>综艺热度</option>
                    <option>剧集观察</option>
                    <option>音乐舞台</option>
                    <option>网络热梗</option>
                    <option>造型红毯</option>
                  </select>
                </div>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="garlic-shield"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {status === 'loading' ? '提交中…' : '免费订阅周报'}
                {status !== 'loading' && <i className="ri-send-plane-line" aria-hidden="true"></i>}
              </button>

              {message && (
                <p
                  className={`mt-4 flex items-start gap-2 text-sm ${
                    status === 'success' ? 'text-primary-300' : 'text-accent-300'
                  }`}
                >
                  <i
                    className={status === 'success' ? 'ri-checkbox-circle-line mt-0.5' : 'ri-error-warning-line mt-0.5'}
                    aria-hidden="true"
                  ></i>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}